import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./lib/api";
import { supabase } from "./lib/supabase";

interface VendorMeModule {
  key: string;
  label: string;
  path: string;
  description: string;
  isGeneric: boolean;
}

interface VendorMeResponse {
  actor: {
    kind: "vendor" | "sgs_staff";
    userId: string;
    email: string;
    displayName: string | null;
    mvSupplierId?: number;
  };
  mvSupplierId?: number;
  modules: VendorMeModule[];
  availableSuppliers?: { mvSupplierId: number; mvSupplierName: string }[];
}

function useVendorMeQuery(mvSupplierId: number | undefined) {
  return useQuery({
    queryKey: ["vendor-me", mvSupplierId ?? "none"],
    queryFn: () =>
      apiFetch<VendorMeResponse>("/api/vendor/me", {
        query:
          mvSupplierId != null && Number.isFinite(mvSupplierId)
            ? { mvSupplierId }
            : undefined,
      }),
  });
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!supabase) {
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!cancelled && !data.session) {
        navigate(`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  if (!supabase) {
    return (
      <div className="mx-auto max-w-lg p-8 text-slate-700">
        <h1 className="text-xl font-semibold">Configuration</h1>
        <p className="mt-2 text-sm">Supabase is not configured for this build.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [messageKind, setMessageKind] = useState<"info" | "error">("info");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) {
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate("/", { replace: true });
      }
    });
  }, [navigate]);

  async function sendMagicLink() {
    if (busy || email.trim() === "") {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await apiFetch<{ ok: boolean; emailSent: boolean }>("/api/vendor/auth/magic-link", {
        method: "POST",
        body: { email: email.trim() },
      });
      setMessageKind("info");
      setMessage("If that email is registered for the vendor portal, a sign-in link is on the way.");
    } catch (error) {
      setMessageKind("error");
      setMessage(error instanceof Error ? error.message : "Could not send sign-in link.");
    } finally {
      setBusy(false);
    }
  }

  function handleMagicLinkSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void sendMagicLink();
  }

  async function signInWithGoogle() {
    if (!supabase) {
      return;
    }
    setBusy(true);
    setMessage(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    setBusy(false);
    if (error) {
      setMessageKind("error");
      setMessage(error.message);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-12">
      <h1 className="text-2xl font-semibold text-slate-900">Vendor portal</h1>
      <div className="mt-8 space-y-4">
        <form className="space-y-4" onSubmit={handleMagicLinkSubmit}>
          <label className="block text-sm font-medium text-slate-700" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={busy || email.trim() === ""}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Send email link
          </button>
        </form>
        <button
          type="button"
          disabled={busy}
          onClick={() => void signInWithGoogle()}
          className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50"
        >
          Sign in as SGS staff
        </button>
        {message != null ? (
          <p
            className={
              messageKind === "error"
                ? "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                : "text-sm text-slate-700"
            }
          >
            {message}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      return;
    }
    const supabaseClient = supabase;
    let cancelled = false;

    const goHome = () => {
      if (!cancelled) {
        navigate("/", { replace: true });
      }
    };

    void (async () => {
      const url = new URL(window.location.href);
      const query = new URLSearchParams(url.search);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ""));

      const authError = query.get("error_description") ?? query.get("error") ?? hash.get("error_description");
      if (authError) {
        if (!cancelled) {
          setError(authError);
        }
        return;
      }

      const code = query.get("code");
      if (code) {
        const { error: exchangeError } = await supabaseClient.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          if (!cancelled) {
            setError(exchangeError.message);
          }
          return;
        }
      }

      const tokenHash = query.get("token_hash") ?? hash.get("token_hash");
      const otpType = query.get("type") ?? hash.get("type");
      if (tokenHash && otpType) {
        const allowedTypes = new Set(["invite", "signup", "magiclink", "recovery", "email"]);
        if (allowedTypes.has(otpType)) {
          const { error: verifyError } = await supabaseClient.auth.verifyOtp({
            token_hash: tokenHash,
            type: otpType as "invite" | "signup" | "magiclink" | "recovery" | "email",
          });
          if (verifyError) {
            if (!cancelled) {
              setError(verifyError.message);
            }
            return;
          }
        }
      }

      const { data: initial } = await supabaseClient.auth.getSession();
      if (initial.session) {
        goHome();
        return;
      }

      const { data: listener } = supabaseClient.auth.onAuthStateChange((event, session) => {
        if (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED")) {
          listener.subscription.unsubscribe();
          goHome();
        }
      });

      window.setTimeout(() => {
        listener.subscription.unsubscribe();
        void supabaseClient.auth.getSession().then(({ data }) => {
          if (cancelled) {
            return;
          }
          if (data.session) {
            goHome();
            return;
          }
          setError("Sign-in could not be completed. Use Send email link below or open the invite link again.");
        });
      }, 4000);
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error != null) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-4 py-12">
        <h1 className="text-xl font-semibold text-slate-900">Sign-in problem</h1>
        <p className="mt-3 text-sm text-slate-700">{error}</p>
        <Link
          to="/sign-in"
          className="mt-6 inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center text-slate-600">
      <p>Finishing sign-in…</p>
    </div>
  );
}

function LogOutButton({ className = "" }: { className?: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  return (
    <button
      type="button"
      disabled={busy || !supabase}
      className={`rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 ${className}`}
      onClick={() => {
        setBusy(true);
        void supabase?.auth.signOut().finally(() => {
          navigate("/sign-in", { replace: true });
        });
      }}
    >
      {busy ? "Logging out..." : "Log out"}
    </button>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const meQuery = useVendorMeQuery(undefined);

  useEffect(() => {
    if (!meQuery.data) {
      return;
    }
    const me = meQuery.data;
    if (me.actor.kind === "vendor" && me.mvSupplierId != null) {
      navigate(`/v/${me.mvSupplierId}/team`, { replace: true });
    }
  }, [meQuery.data, navigate]);

  if (meQuery.isLoading || meQuery.isFetching) {
    return (
      <div className="flex min-h-full items-center justify-center text-slate-600">
        <p>Loading…</p>
      </div>
    );
  }

  if (meQuery.isError) {
    return (
      <div className="mx-auto max-w-lg p-8 text-red-700">
        <p>{meQuery.error instanceof Error ? meQuery.error.message : "Failed to load profile."}</p>
      </div>
    );
  }

  const me = meQuery.data;
  if (!me) {
    return null;
  }

  if (me.actor.kind === "vendor") {
    return (
      <div className="flex min-h-full items-center justify-center text-slate-600">
        <p>Redirecting…</p>
      </div>
    );
  }

  const suppliers = me.availableSuppliers ?? [];
  if (suppliers.length === 0) {
    return (
      <div className="mx-auto max-w-lg p-8 text-slate-700">
        <h1 className="text-xl font-semibold">Vendor portal</h1>
        <p className="mt-2 text-sm">No suppliers are set up for the vendor portal yet.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Choose supplier</h1>
          <p className="mt-2 text-sm text-slate-600">
            You are signed in as Smart Grid Solutions staff. Pick a supplier to open their vendor portal view.
          </p>
        </div>
        <LogOutButton />
      </div>
      <ul className="mt-6 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white shadow-sm">
        {suppliers.map((row) => (
          <li key={row.mvSupplierId}>
            <Link
              to={`/v/${row.mvSupplierId}/team`}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-slate-50"
            >
              <span className="font-medium text-slate-900">{row.mvSupplierName}</span>
              <span className="text-slate-500">Megaventory supplier id {row.mvSupplierId}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface PortalOutletContext {
  mvSupplierId: number;
  me: VendorMeResponse;
}

type SortDirection = "asc" | "desc";

interface TableSortState {
  key: string | null;
  direction: SortDirection;
}

function parseUsMonthDayYear(value: string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "—") {
    return null;
  }
  const parts = trimmed.split("/");
  if (parts.length !== 3) {
    return null;
  }
  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);
  if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) {
    return null;
  }
  const time = new Date(year, month - 1, day).getTime();
  return Number.isFinite(time) ? time : null;
}

function compareNullableNumbers(left: number | null, right: number | null): number {
  if (left == null && right == null) {
    return 0;
  }
  if (left == null) {
    return 1;
  }
  if (right == null) {
    return -1;
  }
  return left - right;
}

function SortableTableHeader(props: {
  label: string;
  columnKey: string;
  sort: TableSortState;
  onSort: (columnKey: string) => void;
  className?: string;
}) {
  const { label, columnKey, sort, onSort, className = "" } = props;
  const active = sort.key === columnKey;
  const alignRight = className.includes("text-right");
  return (
    <th
      scope="col"
      className={className}
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined}
    >
      <button
        type="button"
        className={`inline-flex w-full items-center gap-1 font-medium text-slate-600 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${alignRight ? "justify-end" : "justify-start"}`}
        onClick={() => onSort(columnKey)}
      >
        {alignRight ? (
          <>
            <span className="min-w-[1em] select-none text-xs tabular-nums text-slate-500" aria-hidden>
              {active ? (sort.direction === "asc" ? "▲" : "▼") : ""}
            </span>
            <span>{label}</span>
          </>
        ) : (
          <>
            <span>{label}</span>
            <span className="min-w-[1em] select-none text-xs tabular-nums text-slate-500" aria-hidden>
              {active ? (sort.direction === "asc" ? "▲" : "▼") : ""}
            </span>
          </>
        )}
      </button>
    </th>
  );
}

function useResettableTableSort(resetToken: string | number) {
  const [sort, setSort] = useState<TableSortState>({ key: null, direction: "asc" });
  useEffect(() => {
    setSort({ key: null, direction: "asc" });
  }, [resetToken]);
  const onSort = (columnKey: string) => {
    setSort((previous) => {
      if (previous.key !== columnKey) {
        return { key: columnKey, direction: "asc" };
      }
      return { key: columnKey, direction: previous.direction === "asc" ? "desc" : "asc" };
    });
  };
  return [sort, onSort] as const;
}

function PortalLayout() {
  const { mvSupplierId: mvRaw } = useParams();
  const mvSupplierId = mvRaw != null && /^\d+$/.test(mvRaw) ? Number.parseInt(mvRaw, 10) : NaN;
  const meQuery = useVendorMeQuery(Number.isFinite(mvSupplierId) ? mvSupplierId : undefined);

  if (!Number.isFinite(mvSupplierId) || mvSupplierId <= 0) {
    return <Navigate to="/" replace />;
  }

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center text-slate-600">
        <p>Loading…</p>
      </div>
    );
  }

  if (meQuery.isError || !meQuery.data) {
    return (
      <div className="mx-auto max-w-lg p-8 text-red-700">
        <p>{meQuery.error instanceof Error ? meQuery.error.message : "Failed to load portal."}</p>
        <Link to="/" className="mt-4 inline-block text-sm text-blue-600 underline">
          Back to home
        </Link>
      </div>
    );
  }

  const me = meQuery.data;
  const modules = me.modules;
  const isSgs = me.actor.kind === "sgs_staff";

  return (
    <div className="min-h-full">
      {isSgs ? (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-950">
          Viewing as supplier {mvSupplierId} · Smart Grid Solutions staff troubleshooting mode
        </div>
      ) : null}
      <div className="mx-auto flex max-w-7xl gap-4 px-4 py-6 lg:gap-6">
        <nav className="w-44 shrink-0 space-y-1 lg:w-52">
          <p className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Modules</p>
          {modules.map((mod) => {
            const to = `/v/${mvSupplierId}${mod.path}`;
            return (
              <Link
                key={mod.key}
                to={to}
                className="block rounded-md px-2 py-1.5 text-sm text-slate-800 hover:bg-slate-100"
              >
                {mod.label}
              </Link>
            );
          })}
          <div className="mt-6 rounded-md border border-slate-200 bg-white p-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signed in</p>
            <p className="mt-1 truncate text-xs text-slate-600">{me.actor.email}</p>
            <LogOutButton className="mt-3 w-full justify-center" />
          </div>
        </nav>
        <main className="min-w-0 flex-1">
          <Outlet context={{ mvSupplierId, me } as PortalOutletContext} />
        </main>
      </div>
    </div>
  );
}

function TeamPage() {
  const { mvSupplierId } = useOutletContext<PortalOutletContext>();
  const [sort, onSort] = useResettableTableSort(mvSupplierId);
  const rowsQuery = useQuery({
    queryKey: ["vendor-team", mvSupplierId],
    queryFn: () =>
      apiFetch<{
        users: { userId: string; email: string | null; displayName: string | null; disabledAt: string | null }[];
      }>(`/api/vendor/${mvSupplierId}/team`),
  });

  const users = (rowsQuery.data?.users ?? []).filter((user) => user.disabledAt == null);
  const sortedUsers = useMemo(() => sortVendorTeamUsers(users, sort), [users, sort]);

  if (rowsQuery.isLoading) {
    return <p className="text-slate-600">Loading team…</p>;
  }
  if (rowsQuery.isError) {
    return <p className="text-red-600">{rowsQuery.error instanceof Error ? rowsQuery.error.message : "Error"}</p>;
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900">Team</h1>
      <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <SortableTableHeader label="Email" columnKey="email" sort={sort} onSort={onSort} className="px-3 py-2" />
              <SortableTableHeader
                label="Display name"
                columnKey="displayName"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2"
              />
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr key={user.userId} className="border-t border-slate-100">
                <td className="px-3 py-2">{user.email ?? "—"}</td>
                <td className="px-3 py-2">{user.displayName ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function pottingPurchaseOrderDisplayNumber(row: {
  purchase_order_number: string | null;
  purchase_order_mv_number: string | null;
  pending_megaventory?: boolean;
}): string {
  if (row.pending_megaventory === true) {
    return "Draft";
  }
  const primary = row.purchase_order_number?.trim();
  const mv = row.purchase_order_mv_number?.trim();
  if (primary && /^PRE-POT-/i.test(primary) && mv) {
    return mv;
  }
  if (primary) {
    return primary;
  }
  return mv ? mv : "—";
}

function pottingGradeBucketsTotal(row: {
  grade_a: number;
  grade_b: number;
  grade_c: number;
  grade_fail: number;
}): number {
  return row.grade_a + row.grade_b + row.grade_c + row.grade_fail;
}

function PottingGradeCountWithShareBar(props: {
  count: number;
  bucketTotal: number;
  barColorClass: string;
}) {
  const { count, bucketTotal, barColorClass } = props;
  const pct = bucketTotal > 0 ? Math.min(100, Math.round((100 * count) / bucketTotal)) : 0;
  const pctLabel = `${String(pct).padStart(2, "0")}%`;
  return (
    <span className="inline-flex items-center gap-2 tabular-nums text-slate-800">
      <div
        className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-200"
        title={pctLabel}
        aria-label={pctLabel}
      >
        <div className={`h-full rounded-full ${barColorClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="inline-block w-[3ch] shrink-0 text-right">{count}</span>
    </span>
  );
}

interface PottingSummaryOrderRow {
  purchase_order_id: string;
  purchase_order_number: string | null;
  purchase_order_mv_number: string | null;
  total: number;
  invoiced: number;
  graded: number;
  grade_a: number;
  grade_b: number;
  grade_c: number;
  grade_fail: number;
  sent_at: string | null;
  has_rework: boolean;
  potting_order_kind?: string;
  pending_megaventory?: boolean;
  draft_kind?: "initial" | "rework";
}

function sortPottingSummaryOrders(rows: PottingSummaryOrderRow[], sort: TableSortState): PottingSummaryOrderRow[] {
  if (sort.key == null) {
    return rows;
  }
  const multiplier = sort.direction === "asc" ? 1 : -1;
  const copy = [...rows];
  copy.sort((left, right) => {
    let comparison = 0;
    switch (sort.key) {
      case "po": {
        const leftLabel = pottingPurchaseOrderDisplayNumber(left);
        const rightLabel = pottingPurchaseOrderDisplayNumber(right);
        comparison = leftLabel.localeCompare(rightLabel, undefined, { sensitivity: "base", numeric: true });
        break;
      }
      case "total":
        comparison = left.total - right.total;
        break;
      case "invoiced":
        comparison = left.invoiced - right.invoiced;
        break;
      case "graded":
        comparison = left.graded - right.graded;
        break;
      case "grade_a":
        comparison = left.grade_a - right.grade_a;
        break;
      case "grade_b":
        comparison = left.grade_b - right.grade_b;
        break;
      case "grade_c":
        comparison = left.grade_c - right.grade_c;
        break;
      case "grade_fail":
        comparison = left.grade_fail - right.grade_fail;
        break;
      case "sent_at":
        comparison = compareNullableNumbers(parseUsMonthDayYear(left.sent_at), parseUsMonthDayYear(right.sent_at));
        if (comparison === 0) {
          comparison = (left.sent_at ?? "").localeCompare(right.sent_at ?? "");
        }
        break;
      default:
        return 0;
    }
    if (comparison !== 0) {
      return multiplier * comparison;
    }
    return left.purchase_order_id.localeCompare(right.purchase_order_id, undefined, { numeric: true });
  });
  return copy;
}

interface VendorTeamUserRow {
  userId: string;
  email: string | null;
  displayName: string | null;
  disabledAt: string | null;
}

function sortVendorTeamUsers(rows: VendorTeamUserRow[], sort: TableSortState): VendorTeamUserRow[] {
  if (sort.key == null) {
    return rows;
  }
  const multiplier = sort.direction === "asc" ? 1 : -1;
  const copy = [...rows];
  copy.sort((left, right) => {
    let comparison = 0;
    if (sort.key === "email") {
      comparison = (left.email ?? "").localeCompare(right.email ?? "", undefined, { sensitivity: "base" });
    } else if (sort.key === "displayName") {
      comparison = (left.displayName ?? "").localeCompare(right.displayName ?? "", undefined, { sensitivity: "base" });
    }
    if (comparison !== 0) {
      return multiplier * comparison;
    }
    return left.userId.localeCompare(right.userId);
  });
  return copy;
}

interface PottingDetailLineRow {
  id: string;
  serial: string | null;
  sku: string | null;
  potting_grade: string | null;
  graded_at: string | null;
  fail_reason: string | null;
}

function sortPottingDetailLines(lines: PottingDetailLineRow[], sort: TableSortState): PottingDetailLineRow[] {
  if (sort.key == null) {
    return lines;
  }
  const multiplier = sort.direction === "asc" ? 1 : -1;
  const copy = [...lines];
  copy.sort((left, right) => {
    let comparison = 0;
    switch (sort.key) {
      case "serial":
        comparison = (left.serial ?? "")
          .trim()
          .localeCompare((right.serial ?? "").trim(), undefined, { sensitivity: "base", numeric: true });
        break;
      case "sku":
        comparison = (left.sku ?? "")
          .trim()
          .localeCompare((right.sku ?? "").trim(), undefined, { sensitivity: "base", numeric: true });
        break;
      case "grade":
        comparison = (left.potting_grade ?? "").localeCompare(right.potting_grade ?? "", undefined, {
          sensitivity: "base",
        });
        break;
      case "graded_at":
        comparison = compareNullableNumbers(parseUsMonthDayYear(left.graded_at), parseUsMonthDayYear(right.graded_at));
        if (comparison === 0) {
          comparison = (left.graded_at ?? "").localeCompare(right.graded_at ?? "");
        }
        break;
      case "fail_reason":
        comparison = (left.fail_reason ?? "").localeCompare(right.fail_reason ?? "", undefined, {
          sensitivity: "base",
        });
        break;
      default:
        return 0;
    }
    if (comparison !== 0) {
      return multiplier * comparison;
    }
    return left.id.localeCompare(right.id, undefined, { numeric: true });
  });
  return copy;
}

function PottingSection() {
  const portalContext = useOutletContext<PortalOutletContext>();

  return (
    <div>
      <h1 className="text-xl font-semibold text-slate-900">Potting grading</h1>
      <Outlet context={portalContext} />
    </div>
  );
}

function PottingOrderSummary() {
  const { mvSupplierId } = useOutletContext<PortalOutletContext>();
  const [sort, onSort] = useResettableTableSort(mvSupplierId);
  const summary = useQuery({
    queryKey: ["vendor-potting-summary", mvSupplierId],
    queryFn: () =>
      apiFetch<{
        orders: PottingSummaryOrderRow[];
      }>(`/api/vendor/${mvSupplierId}/potting`),
  });

  const orders = summary.data?.orders ?? [];
  const sortedOrders = useMemo(() => sortPottingSummaryOrders(orders, sort), [orders, sort]);

  if (summary.isLoading) {
    return <p className="mt-4 text-slate-600">Loading…</p>;
  }
  if (summary.isError) {
    return (
      <p className="mt-4 text-red-600">{summary.error instanceof Error ? summary.error.message : "Error"}</p>
    );
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <SortableTableHeader
              label="Purchase order"
              columnKey="po"
              sort={sort}
              onSort={onSort}
              className="w-32 px-2 py-2"
            />
            <SortableTableHeader label="Total" columnKey="total" sort={sort} onSort={onSort} className="w-16 px-2 py-2 text-right" />
            <SortableTableHeader
              label="Invoiced"
              columnKey="invoiced"
              sort={sort}
              onSort={onSort}
              className="w-20 px-2 py-2 text-right"
            />
            <SortableTableHeader
              label="Graded"
              columnKey="graded"
              sort={sort}
              onSort={onSort}
              className="w-16 px-2 py-2 text-right"
            />
            <SortableTableHeader label="A" columnKey="grade_a" sort={sort} onSort={onSort} className="w-24 px-2 py-2 text-right" />
            <SortableTableHeader label="B" columnKey="grade_b" sort={sort} onSort={onSort} className="w-24 px-2 py-2 text-right" />
            <SortableTableHeader label="C" columnKey="grade_c" sort={sort} onSort={onSort} className="w-24 px-2 py-2 text-right" />
            <SortableTableHeader
              label="Fail"
              columnKey="grade_fail"
              sort={sort}
              onSort={onSort}
              className="w-24 px-2 py-2 text-right"
            />
            <SortableTableHeader
              label="Sent at"
              columnKey="sent_at"
              sort={sort}
              onSort={onSort}
              className="w-20 px-2 py-2 text-right"
            />
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr>
              <td className="px-3 py-4 text-slate-500" colSpan={9}>
                No potting or rework records yet for this supplier.
              </td>
            </tr>
          ) : (
            sortedOrders.map((row) => {
              const bucketTotal = pottingGradeBucketsTotal(row);
              const detailTo =
                row.pending_megaventory === true && row.draft_kind != null
                  ? `draft/${row.draft_kind}`
                  : `order/${row.purchase_order_id}`;
              return (
                <tr key={row.purchase_order_id} className="border-t border-slate-100">
                  <td className="w-32 px-2 py-2">
                    <div className="flex flex-nowrap items-center gap-2">
                      <Link
                        to={detailTo}
                        className="shrink-0 font-medium text-blue-700 underline decoration-blue-400 underline-offset-2 hover:text-blue-900"
                        title={
                          row.pending_megaventory === true
                            ? "Megaventory purchase order has not been created for this batch yet"
                            : undefined
                        }
                      >
                        {pottingPurchaseOrderDisplayNumber(row)}
                      </Link>
                      {row.has_rework ? (
                        <span
                          className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-950 ring-1 ring-inset ring-amber-200"
                          title="This purchase order is a rework purchase order (input into rework)"
                        >
                          Rework
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="w-16 px-2 py-2 text-right text-slate-700 tabular-nums">{row.total}</td>
                  <td className="w-20 px-2 py-2 text-right text-slate-700 tabular-nums">{row.invoiced}</td>
                  <td className="w-16 px-2 py-2 text-right tabular-nums">{row.graded}</td>
                  <td className="w-24 px-2 py-2 text-right">
                    <PottingGradeCountWithShareBar
                      count={row.grade_a}
                      bucketTotal={bucketTotal}
                      barColorClass="bg-emerald-500"
                    />
                  </td>
                  <td className="w-24 px-2 py-2 text-right">
                    <PottingGradeCountWithShareBar
                      count={row.grade_b}
                      bucketTotal={bucketTotal}
                      barColorClass="bg-sky-500"
                    />
                  </td>
                  <td className="w-24 px-2 py-2 text-right">
                    <PottingGradeCountWithShareBar
                      count={row.grade_c}
                      bucketTotal={bucketTotal}
                      barColorClass="bg-amber-500"
                    />
                  </td>
                  <td className="w-24 px-2 py-2 text-right">
                    <PottingGradeCountWithShareBar
                      count={row.grade_fail}
                      bucketTotal={bucketTotal}
                      barColorClass="bg-rose-500"
                    />
                  </td>
                  <td className="w-20 px-2 py-2 text-right tabular-nums">{row.sent_at ?? "—"}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function PottingDraftOrderDetail() {
  const { mvSupplierId } = useOutletContext<PortalOutletContext>();
  const { draftKind } = useParams();
  const draftOk = draftKind === "initial" || draftKind === "rework";
  const [sort, onSort] = useResettableTableSort(`${mvSupplierId}:draft:${draftKind ?? ""}`);
  const detail = useQuery({
    queryKey: ["vendor-potting-draft", mvSupplierId, draftKind ?? ""],
    enabled: draftOk,
    queryFn: () =>
      apiFetch<{
        purchase_order_id: string;
        purchase_order_number: string | null;
        purchase_order_mv_number: string | null;
        pending_megaventory?: boolean;
        lines: PottingDetailLineRow[];
      }>(`/api/vendor/${mvSupplierId}/potting/draft/${draftKind}`),
  });

  const lines = detail.data?.lines ?? [];
  const sortedLines = useMemo(() => sortPottingDetailLines(lines, sort), [lines, sort]);

  if (!draftOk) {
    return (
      <p className="mt-4 text-red-600">
        Invalid draft batch.{" "}
        <Link to=".." className="underline">
          Back to summary
        </Link>
      </p>
    );
  }

  if (detail.isLoading) {
    return <p className="mt-4 text-slate-600">Loading…</p>;
  }
  if (detail.isError) {
    return (
      <div className="mt-4 space-y-2">
        <p className="text-red-600">{detail.error instanceof Error ? detail.error.message : "Error"}</p>
        <Link to=".." className="text-sm text-blue-700 underline">
          Back to summary
        </Link>
      </div>
    );
  }

  const head = detail.data;
  if (!head) {
    return null;
  }

  const title = pottingPurchaseOrderDisplayNumber({
    purchase_order_number: head.purchase_order_number,
    purchase_order_mv_number: head.purchase_order_mv_number,
    pending_megaventory: true,
  });

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <Link to=".." className="text-sm text-blue-700 underline">
          ← All purchase orders
        </Link>
        <h2 className="text-lg font-semibold text-slate-900">
          <span className="inline-flex flex-nowrap items-center gap-2">
            <span title="Megaventory purchase order has not been created for this batch yet">{title}</span>
            {draftKind === "rework" ? (
              <span
                className="inline-flex shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-950 ring-1 ring-inset ring-amber-200"
                title="This batch is potting rework (input into rework)"
              >
                Rework
              </span>
            ) : null}
          </span>
        </h2>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <SortableTableHeader
                label="Serial"
                columnKey="serial"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader label="SKU" columnKey="sku" sort={sort} onSort={onSort} className="px-3 py-2 text-right" />
              <SortableTableHeader
                label="Grade"
                columnKey="grade"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader
                label="Graded at"
                columnKey="graded_at"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader
                label="Fail reason"
                columnKey="fail_reason"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2"
              />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={5}>
                  No lines in this draft batch.
                </td>
              </tr>
            ) : (
              sortedLines.map((row) => {
                const gradeNorm = row.potting_grade?.trim().toLowerCase() ?? "";
                const isFail = gradeNorm === "fail";
                return (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                      {row.serial?.trim() ? row.serial.trim() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{row.sku?.trim() ? row.sku : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.potting_grade ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.graded_at ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {isFail ? (row.fail_reason?.trim() ? row.fail_reason.trim() : "—") : "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PottingOrderDetail() {
  const { mvSupplierId } = useOutletContext<PortalOutletContext>();
  const { purchaseOrderId } = useParams();
  const purchaseOrderIdOk = purchaseOrderId != null && /^\d+$/.test(purchaseOrderId);
  const [sort, onSort] = useResettableTableSort(`${mvSupplierId}:${purchaseOrderId ?? ""}`);
  const detail = useQuery({
    queryKey: ["vendor-potting-order", mvSupplierId, purchaseOrderId ?? ""],
    enabled: purchaseOrderIdOk,
    queryFn: () =>
      apiFetch<{
        purchase_order_id: string;
        purchase_order_number: string | null;
        purchase_order_mv_number: string | null;
        lines: PottingDetailLineRow[];
      }>(`/api/vendor/${mvSupplierId}/potting/order/${purchaseOrderId}`),
  });

  const lines = detail.data?.lines ?? [];
  const sortedLines = useMemo(() => sortPottingDetailLines(lines, sort), [lines, sort]);

  if (!purchaseOrderIdOk) {
    return (
      <p className="mt-4 text-red-600">
        Invalid purchase order.{" "}
        <Link to=".." className="underline">
          Back to summary
        </Link>
      </p>
    );
  }

  if (detail.isLoading) {
    return <p className="mt-4 text-slate-600">Loading…</p>;
  }
  if (detail.isError) {
    return (
      <div className="mt-4 space-y-2">
        <p className="text-red-600">{detail.error instanceof Error ? detail.error.message : "Error"}</p>
        <Link to=".." className="text-sm text-blue-700 underline">
          Back to summary
        </Link>
      </div>
    );
  }

  const head = detail.data;
  if (!head) {
    return null;
  }

  const title = pottingPurchaseOrderDisplayNumber(head);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <Link to=".." className="text-sm text-blue-700 underline">
          ← All purchase orders
        </Link>
        <h2 className="text-lg font-semibold text-slate-900">
          Purchase order {title}
          {head.purchase_order_number?.trim() &&
          head.purchase_order_mv_number?.trim() &&
          head.purchase_order_number.trim() !== head.purchase_order_mv_number.trim() ? (
            <span className="ml-2 text-sm font-normal text-slate-600">
              Megaventory {head.purchase_order_mv_number.trim()}
            </span>
          ) : null}
        </h2>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <SortableTableHeader
                label="Serial"
                columnKey="serial"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader label="SKU" columnKey="sku" sort={sort} onSort={onSort} className="px-3 py-2 text-right" />
              <SortableTableHeader
                label="Grade"
                columnKey="grade"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader
                label="Graded at"
                columnKey="graded_at"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2 text-right"
              />
              <SortableTableHeader
                label="Fail reason"
                columnKey="fail_reason"
                sort={sort}
                onSort={onSort}
                className="px-3 py-2"
              />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-slate-500" colSpan={5}>
                  No graded lines for this purchase order.
                </td>
              </tr>
            ) : (
              sortedLines.map((row) => {
                const gradeNorm = row.potting_grade?.trim().toLowerCase() ?? "";
                const isFail = gradeNorm === "fail";
                return (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                      {row.serial?.trim() ? row.serial.trim() : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{row.sku?.trim() ? row.sku : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.potting_grade ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.graded_at ?? "—"}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {isFail ? (row.fail_reason?.trim() ? row.fail_reason.trim() : "—") : "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route
        element={
          <RequireSession>
            <Outlet />
          </RequireSession>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/v/:mvSupplierId" element={<PortalLayout />}>
          <Route path="team" element={<TeamPage />} />
          <Route path="potting" element={<PottingSection />}>
            <Route index element={<PottingOrderSummary />} />
            <Route path="draft/:draftKind" element={<PottingDraftOrderDetail />} />
            <Route path="order/:purchaseOrderId" element={<PottingOrderDetail />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
